import {
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
} from 'remotion';

// Theme
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Utils
import { combineClasses } from '@/utils/className';
import { calculateEngagement } from '@/utils';

// Constants
import {
  STATS_ANIM,
  CLAMP_RIGHT,
  FLIP_COUNTER_ANIM,
  ANNIVERSARY_ANIM,
} from '../constants';

const STATS_LAYOUT = {
  ENGAGEMENT: { left: 1369, top: 438, width: 241, height: 50 },
  YEAR_BOX: { left: 1369, top: 498, width: 241, height: 241 },
  MONTH_BOX: { left: 1490, top: 749, width: 241, height: 241 },
  UNDERLINE: { left: 1623, top: 999, width: 108, height: 21 },
} as const;

interface StatsProps {
  onboardingDate?: string;
  showAnniversary?: boolean;
}

// Components
import { EngagedFlipCounter } from '../FlipCounter';
import { TextFlipAnimation } from '@/components/common/TextFlipAnimation';
import { StarAnimation } from '../StarAnimation';

export const Stats = ({
  onboardingDate,
  showAnniversary = false,
}: StatsProps) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Calculate years and months from onboarding date
  const { years, months } = calculateEngagement(onboardingDate);
  // Start transition after flip counter finishes (Month counter ends last)
  const anniversaryAnimStartFrame =
    FLIP_COUNTER_ANIM.MONTH_START +
    FLIP_COUNTER_ANIM.MONTH_DURATION +
    FLIP_COUNTER_ANIM.ANNIVERSARY_FLIP_BUFFER;

  const stagger = spring({
    frame: frame - STATS_ANIM.DELAY,
    fps,
    config: STATS_ANIM.SPRING_CONFIG,
  });

  const scale = interpolate(stagger, [0, 1], [0, 1], CLAMP_RIGHT);

  const opacity = interpolate(stagger, [0, 1], [0, 1], CLAMP_RIGHT);

  // Fade out month counter and underline if it's an anniversary
  // Start fading out when the anniversary animation starts
  const secondaryElementsOpacity = showAnniversary
    ? interpolate(
        frame,
        [anniversaryAnimStartFrame, anniversaryAnimStartFrame + 15],
        [1, 0],
        {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        },
      )
    : 1;

  return (
    <div
      style={{
        transform: `scale(${scale})`,
        transformOrigin: 'center',
        opacity,
      }}
    >
      {/* Engagement Label */}
      <Sequence
        name="Engagement Label"
        className={combineClasses(
          'flex-center flex-col text-white',
          employeeYearInReviewTheme.colors.bgSlateDeep,
        )}
        style={STATS_LAYOUT.ENGAGEMENT}
      >
        {showAnniversary ? (
          <TextFlipAnimation
            startFrame={anniversaryAnimStartFrame}
            fromText="in engaged"
            toText="anniversary"
            duration={{
              fall: ANNIVERSARY_ANIM.FALL_DURATION,
              riseDelay: ANNIVERSARY_ANIM.RISE_DELAY,
              rise: ANNIVERSARY_ANIM.RISE_DURATION,
            }}
            perspective={ANNIVERSARY_ANIM.PERSPECTIVE}
            fromClassName="text-white text-3xl leading-9 tracking-widest"
            toClassName={`text-3xl leading-9 tracking-widest text-primary-950 bg-gold-200`}
          />
        ) : (
          <span className="text-white text-3xl leading-9 tracking-widest">
            in engaged
          </span>
        )}
      </Sequence>

      {/* Star Animation */}
      {showAnniversary && (
        <Sequence
          from={anniversaryAnimStartFrame}
          style={{
            left: STATS_LAYOUT.ENGAGEMENT.left + 25,
            top: STATS_LAYOUT.ENGAGEMENT.top - 80,
            zIndex: 0, // Below Polaroid
          }}
        >
          <StarAnimation />
        </Sequence>
      )}

      <div className="absolute" style={STATS_LAYOUT.YEAR_BOX}>
        <EngagedFlipCounter
          value={years}
          unit="year"
          startFrame={FLIP_COUNTER_ANIM.YEAR_START}
          totalFrames={FLIP_COUNTER_ANIM.YEAR_DURATION}
          className="size-full justify-center"
          showFillAnimation={showAnniversary}
          fillAnimationStartFrame={anniversaryAnimStartFrame}
        />
      </div>

      {/* Month Counter */}
      <div
        className="absolute"
        style={{ ...STATS_LAYOUT.MONTH_BOX, opacity: secondaryElementsOpacity }}
      >
        <EngagedFlipCounter
          value={months}
          unit="month"
          startFrame={FLIP_COUNTER_ANIM.MONTH_START}
          totalFrames={FLIP_COUNTER_ANIM.MONTH_DURATION}
          className="size-full justify-center"
        />
      </div>

      {/* Decorative Underline */}
      <div
        className={combineClasses(
          'absolute',
          employeeYearInReviewTheme.colors.bgSlateDarker,
        )}
        style={{ ...STATS_LAYOUT.UNDERLINE, opacity: secondaryElementsOpacity }}
      />
    </div>
  );
};
